import { useMemo } from 'react';
import { css } from '@emotion/react';
import { SmallModeSummaryCard } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useGetCancellationsMetric } from './hooks/use-get-cancellations-metric';
import { useGetNewPatientsMetric } from './hooks/use-get-new-patients-metric';
import { useGetPractitionerMetric } from './hooks/use-get-practitioner-metric';
import { useGetRecapturedPatientsMetric } from './hooks/use-get-recaptured-patients-metric';
import { usePaNavigate } from './hooks/use-pa-navigate';
import { PracticeAnalyticsInfoTips } from './practice-analytics-info-tips';

type SmallModeCardsProps = {
  verySmallWidth?: boolean;
};

export const SmallModeCards = ({ verySmallWidth }: SmallModeCardsProps) => {
  const { t } = useTranslation('analytics');
  const navigate = usePaNavigate();

  const { aggregatedCancellationsData, isLoading: isLoadingCancellations } = useGetCancellationsMetric();
  const { aggregatedNewPatientsData, isLoading: isLoadingNewPatients } = useGetNewPatientsMetric();
  const { aggregatedRecapturedPatientsData, isLoading: isLoadingRecapturedPatients } = useGetRecapturedPatientsMetric();
  const { aggregatedPractitionerAnalysisData, isLoading: isLoadingPractitionerAnalysis } = useGetPractitionerMetric();

  const { practitionerAcceptedPercent, practitionerTotalUnscheduledTreatment } = useMemo(() => {
    if (!aggregatedPractitionerAnalysisData) {
      return {
        practitionerAcceptedPercent: 0,
        practitionerTotalUnscheduledTreatment: 0,
      };
    }

    const { hygieneTreatmentPlan, restorativeTreatmentPlan } = aggregatedPractitionerAnalysisData;
    const totalAccepted = hygieneTreatmentPlan.totals.accepted + restorativeTreatmentPlan.totals.accepted;
    const totalDiagnosed = hygieneTreatmentPlan.totals.diagnosed + restorativeTreatmentPlan.totals.diagnosed;

    let acceptedPercent = Math.round((totalAccepted / totalDiagnosed) * 100);
    if (isNaN(acceptedPercent) || !isFinite(acceptedPercent)) {
      acceptedPercent = 0;
    }

    return {
      practitionerAcceptedPercent: acceptedPercent,
      practitionerTotalUnscheduledTreatment:
        hygieneTreatmentPlan.totals.unscheduledTreatment + restorativeTreatmentPlan.totals.unscheduledTreatment,
    };
  }, [aggregatedPractitionerAnalysisData]);

  return (
    <section css={styles.wrapper}>
      <SmallModeSummaryCard
        iconName='calendar-discarded'
        infoTop={<PracticeAnalyticsInfoTips tip='cancellations' />}
        isLoading={isLoadingCancellations}
        onClick={() => {
          navigate({
            to: '/analytics/practice/cancelled-appointments',
          });
        }}
        summary={[
          {
            label: t('Patients'),
            value: verySmallWidth
              ? formatters.shortenNumber(aggregatedCancellationsData?.missedAppointments?.totals?.cancelled)
              : formatters.value(aggregatedCancellationsData?.missedAppointments?.totals?.cancelled),
          },
          {
            label: t('Value'),
            value: formatters.shortenCurrency(
              aggregatedCancellationsData?.missedAppointments?.totals?.totalProductionRisk,
              verySmallWidth
            ),
          },
        ]}
        title={t('Cancellations')}
        trackingId='pa-small-mode-cancellations'
      />

      <SmallModeSummaryCard
        iconName='contacts'
        infoTop={<PracticeAnalyticsInfoTips tip='newPatients' />}
        isLoading={isLoadingNewPatients}
        onClick={() => {
          navigate({
            to: '/analytics/practice/new-patients',
          });
        }}
        summary={[
          {
            label: t('Patients'),
            value: verySmallWidth
              ? formatters.shortenNumber(aggregatedNewPatientsData?.newPatients?.totals?.patients)
              : formatters.value(aggregatedNewPatientsData?.newPatients?.totals?.patients),
          },
          {
            label: t('Value'),
            value: formatters.shortenCurrency(
              aggregatedNewPatientsData?.newPatients?.totals?.production,
              verySmallWidth
            ),
          },
        ]}
        title={t('New Patients')}
        trackingId='pa-small-mode-new-patients'
      />

      <SmallModeSummaryCard
        iconName='assigned-user'
        infoTop={<PracticeAnalyticsInfoTips tip='recapturedPatients' />}
        isLoading={isLoadingRecapturedPatients}
        onClick={() => {
          navigate({
            to: '/analytics/practice/recaptured-patients',
          });
        }}
        summary={[
          {
            label: t('Patients'),
            value: verySmallWidth
              ? formatters.shortenNumber(aggregatedRecapturedPatientsData?.recapturedPatients?.totals?.patients)
              : formatters.value(aggregatedRecapturedPatientsData?.recapturedPatients?.totals?.patients),
          },
          {
            label: t('Value'),
            value: formatters.shortenCurrency(
              aggregatedRecapturedPatientsData?.recapturedPatients?.totals?.production,
              verySmallWidth
            ),
          },
        ]}
        title={t('Recaptured')}
        trackingId='pa-small-mode-recaptured-patients'
      />

      <SmallModeSummaryCard
        iconName='user'
        isLoading={isLoadingPractitionerAnalysis}
        onClick={() => {
          navigate({
            to: '/analytics/practice/practitioner-analysis',
          });
        }}
        summary={[
          {
            label: t('Patients'),
            value: formatters.appendPercent(practitionerAcceptedPercent),
          },
          {
            label: t('Value'),
            value: formatters.shortenCurrency(practitionerTotalUnscheduledTreatment, verySmallWidth),
          },
        ]}
        title={t('Practitioner')}
        trackingId='pa-small-mode-practitioner-analysis'
      />
    </section>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    gap: ${theme.spacing(2)};
    flex-wrap: wrap;
    margin-bottom: ${theme.spacing(2)};
  `,
};
